// Minimal analytics wrapper — tracks anonymous PRODUCT USAGE ONLY.
//
// Hard rule: never pass amounts, categories, notes, member names, client
// names, or anything else that came out of someone's ledger. Only counts
// and types (e.g. "a personal profile was created") ever leave the device.
// This keeps the "private by default" pitch honest while still giving you
// real numbers: opens, installs, and how many people get past onboarding.
//
// Uses PostHog because it has a real free tier (1M events/month) rather
// than a time-limited trial. Swap the import/init below for any other
// provider if you'd rather use something else.

import posthog from "posthog-js";

// 1. Create a free account at https://posthog.com
// 2. Project Settings -> Project API Key. It starts with phc_.
// 3. Paste it below. Leave as-is to run with analytics fully disabled.
//
// IMPORTANT: PostHog has the same public/private split as Supabase.
// phc_... (Project API key) is safe here — it's the client-side capture
// token, meant to ship in browser code. phx_... (Personal API key) is
// private, tied to your account, and must never go here — same mistake
// as pasting a Supabase secret key into supabaseClient.js.
const POSTHOG_KEY = "YOUR_POSTHOG_PROJECT_KEY";
const POSTHOG_HOST = "https://us.i.posthog.com";

const looksLikePersonalKey = POSTHOG_KEY.startsWith("phx_");

if (looksLikePersonalKey) {
  console.error(
    "analytics.js: a Personal API key (phx_...) is in the Project API key slot. " +
    "Analytics has been disabled to avoid shipping it to the browser. Replace it with the " +
    "key starting phc_... from PostHog's Project Settings."
  );
}

// Shown wherever the app wants to surface it — same pattern as
// supabaseClient.js's configWarning.
export const configWarning = looksLikePersonalKey
  ? "A PostHog Personal API key is pasted where the Project API key belongs, in src/analytics.js. Analytics is disabled until that's fixed — swap in the key starting phc_... instead."
  : null;

let ready = false;

// People can switch usage counts off in Settings > Privacy. The choice is kept on the device; when it is off, the analytics
// library is never even started.
const OPT_KEY = "bb-analytics-optout";
export const analyticsConfigured = !looksLikePersonalKey && !!POSTHOG_KEY && !POSTHOG_KEY.startsWith("YOUR_");
export function analyticsOptedOut() { try { return localStorage.getItem(OPT_KEY) === "1"; } catch (e) { return false; } }
export function setAnalyticsOptOut(out) {
  try { localStorage.setItem(OPT_KEY, out ? "1" : "0"); } catch (e) { /* ignore */ }
  try {
    if (out && ready) { posthog.opt_out_capturing(); posthog.reset(); }
    else if (!out) { if (ready) posthog.opt_in_capturing(); else initAnalytics(); }
  } catch (e) { /* never let this affect the app */ }
}

export function initAnalytics() {
  if (ready || !analyticsConfigured || analyticsOptedOut()) return;
  try {
    posthog.init(POSTHOG_KEY, {
      api_host: POSTHOG_HOST,
      autocapture: false, // no automatic click/form tracking — only explicit events below
      capture_pageview: false,
      persistence: "localStorage",
    });
    ready = true;
  } catch (e) {
    console.error("analytics.js: failed to initialize PostHog", e);
  }
}

export function track(event, props = {}) {
  if (!ready || analyticsOptedOut()) return;
  try {
    posthog.capture(event, props);
  } catch (e) {
    // never let a tracking failure affect the app itself
  }
}

export function trackInstall() {
  track("pwa_installed");
}
